import { useState, type FormEvent } from 'react';
import { ArrowUpCircle, Award, BadgeCheck, CheckCircle2, Clock, Copy, Download, Send, Sparkles, Wallet } from 'lucide-react';
import { CertificateBrowser, type RecordLite } from '@ck/ui';
import { dateTimeLabel, usd, type PublicTenant } from '@ck/sdk';
import { api } from './api';
import { Badge, Empty, Failed, Loading, Note, PageHead, useAction, useApi } from './ui';

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = Record<string, any>;
interface Mk { requests: Any[]; accounts: Any[]; withdrawals: Any[]; certificates: Any[]; catalog: { name: string; sizes: number[] }[]; wallets: string[]; name: string }

/**
 * Marketing account: a demo account for an IB's content. It is not real money and lives only here.
 * The IB upgrades phases and withdraws on their own; withdrawals approve themselves instantly.
 */
export function MarketingAccount({ tenant }: { tenant: PublicTenant }) {
  const d = useApi(() => api.get<Mk>('/v1/partner/marketing'));
  const [celebrate, setCelebrate] = useState<string | null>(null);
  if (d.error) return <Failed error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const m = d.data;
  const open = m.requests.find((r) => ['new', 'forwarded'].includes(r.status));
  const done = () => d.reload();
  return (
    <>
      <PageHead kicker="IB tools" title="Marketing account" lead="A special demo account for your posts, videos and live sessions. Upgrade it and withdraw whenever you want; withdrawals are approved instantly. It is for marketing only: no real money moves." />
      {celebrate && <div className="mk-celebrate" role="status"><Sparkles /><b>{celebrate}</b><button className="link" onClick={() => setCelebrate(null)}>Close</button></div>}
      {m.accounts.length === 0 ? (
        open ? <WaitingRequest r={open} /> : <RequestForm catalog={m.catalog} done={done} declined={m.requests.find((r) => r.status === 'declined')} />
      ) : (
        <>
          <div className="mk-accounts">{m.accounts.map((a) => <AccountCard key={a.id} a={a} />)}</div>
          {m.accounts.map((a) => <Journey key={a.id} a={a} />)}
          <div className="mk-tools">
            <Upgrade accounts={m.accounts} done={(msg) => { setCelebrate(msg); done(); }} />
            <Withdraw accounts={m.accounts} wallets={m.wallets} done={(msg) => { setCelebrate(msg); done(); }} />
          </div>
          {!open && <details className="panel mk-more"><summary>Request another marketing account</summary><RequestForm catalog={m.catalog} done={done} /></details>}
          {open && <WaitingRequest r={open} />}
        </>
      )}
      <Certificates list={m.certificates} name={m.name} tenant={tenant} accounts={m.accounts} />
      {m.withdrawals.length > 0 && (
        <section className="panel"><h3>Marketing withdrawals</h3>
          <div className="tw"><table className="tbl"><thead><tr><th>When</th><th>Account</th><th>Method</th><th>To</th><th className="r">Amount</th><th>Status</th></tr></thead>
            <tbody>{m.withdrawals.map((w) => <tr key={w.id}><td>{dateTimeLabel(w.created_at)}</td><td className="mono">{w.login}</td><td>{w.method}</td><td className="mono">{String(w.destination).slice(0, 6)}…{String(w.destination).slice(-4)}</td><td className="r">{usd(w.amount)}</td><td><Badge status="paid" /></td></tr>)}</tbody></table></div>
        </section>
      )}
    </>
  );
}

function RequestForm({ catalog, done, declined }: { catalog: Mk['catalog']; done: () => void; declined?: Any }) {
  const [f, setF] = useState({ planName: catalog[0]?.name ?? '', size: catalog[0]?.sizes.at(-1) ?? 100000, purpose: '' });
  const a = useAction();
  const sizes = catalog.find((c) => c.name === f.planName)?.sizes ?? [];
  async function send(e: FormEvent) { e.preventDefault(); if (await a.run(() => api.post('/v1/partner/marketing/requests', f))) done(); }
  return (
    <form className="panel form mk-request" onSubmit={send}>
      <h3>Ask for a marketing account</h3>
      <p className="muted">Our support team sends your request to your manager. You get the account here once it is approved.</p>
      {declined && <Note kind="info">Your last request was declined{declined.note ? `: ${declined.note}` : ''}. You can ask again.</Note>}
      <div className="grid2">
        <label>Challenge<select value={f.planName} onChange={(e) => { const c = catalog.find((x) => x.name === e.target.value); setF({ ...f, planName: e.target.value, size: c?.sizes.at(-1) ?? f.size }); }}>{catalog.map((c) => <option key={c.name}>{c.name}</option>)}</select></label>
        <label>Account size<select value={f.size} onChange={(e) => setF({ ...f, size: Number(e.target.value) })}>{sizes.map((s) => <option key={s} value={s}>{usd(s, 0)}</option>)}</select></label>
      </div>
      <label>What will you use it for? <small>Optional</small><textarea rows={2} value={f.purpose} placeholder="e.g. weekly TikTok videos and a Telegram live session" onChange={(e) => setF({ ...f, purpose: e.target.value })} /></label>
      {a.error && <Note kind="err">{a.error}</Note>}
      <button className="btn primary" disabled={a.busy}><Send />Send request</button>
    </form>
  );
}

function WaitingRequest({ r }: { r: Any }) {
  return (
    <div className="panel mk-wait"><Clock /><div><b>Your request is with our team</b><span>{r.plan_name} {usd(r.size, 0)} · {r.status === 'forwarded' ? 'Your manager is reviewing it.' : 'Customer support is routing it to your manager.'}</span></div></div>
  );
}

function AccountCard({ a }: { a: Any }) {
  const funded = a.status === 'funded';
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(a.login); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* blocked */ } };
  return (
    <article className={`mk-card${funded ? ' funded' : ''}`}>
      <header>
        <div><small className="mono">{a.planName} ${a.size >= 1000 ? `${a.size / 1000}K` : a.size}</small><h3>{a.phase}</h3></div>
        <span className={`mk-pill${funded ? ' ok' : ''}`}><i />{funded ? 'Funded' : 'In progress'}</span>
      </header>
      <div className="mk-facts">
        <span><small>Login</small><b className="mono">{a.login}<button type="button" className="mk-copy" aria-label="Copy login" onClick={() => void copy()}>{copied ? <CheckCircle2 /> : <Copy />}</button></b></span>
        <span><small>Account type</small><b>Marketing demo</b></span>
        <span><small>Balance</small><b className="mono">{usd(a.balance, 0)}</b></span>
        <span><small>Equity</small><b className="mono">{usd(a.balance, 0)}</b></span>
      </div>
    </article>
  );
}

function Journey({ a }: { a: Any }) {
  const phases: string[] = a.phases;
  return (
    <section className="panel mk-journey" aria-label={`Phases for ${a.login}`}>
      <h3><BadgeCheck />Phases <small className="muted mono">{a.login}</small></h3>
      <ol className="mk-steps">{phases.map((p, i) => <li key={p} className={i < a.phaseIndex ? 'done' : i === a.phaseIndex ? 'on' : ''}><i>{i < a.phaseIndex ? <CheckCircle2 /> : i + 1}</i><span>{p}</span></li>)}</ol>
    </section>
  );
}

function Upgrade({ accounts, done }: { accounts: Any[]; done: (msg: string) => void }) {
  const [f, setF] = useState({ login: accounts[0]?.login ?? '', phaseIndex: Math.min((accounts[0]?.phaseIndex ?? 0) + 1, (accounts[0]?.phases.length ?? 1) - 1) });
  const a = useAction();
  const acc = accounts.find((x) => x.login.toUpperCase() === f.login.trim().toUpperCase());
  async function go(e: FormEvent) {
    e.preventDefault();
    const r = await a.run(() => api.post<{ account: Any }>('/v1/partner/marketing/upgrade', f));
    if (r) done(/fund/i.test(r.account.phase) ? `${r.account.login} is now Funded. Your certificate is ready below.` : `${r.account.login} moved to ${r.account.phase}.`);
  }
  return (
    <form className="panel form" onSubmit={go}>
      <h3><ArrowUpCircle />Upgrade a phase</h3>
      <label>Account number<input className="mono" list="mk-logins" required value={f.login} onChange={(e) => setF({ ...f, login: e.target.value })} placeholder="MKT-123456" /><datalist id="mk-logins">{accounts.map((x) => <option key={x.id} value={x.login} />)}</datalist></label>
      <label>Move to<select value={f.phaseIndex} onChange={(e) => setF({ ...f, phaseIndex: Number(e.target.value) })} disabled={!acc}>{(acc?.phases ?? []).map((p: string, i: number) => <option key={p} value={i} disabled={i === acc?.phaseIndex}>{p}{i === acc?.phaseIndex ? ' (now)' : ''}</option>)}</select></label>
      {a.error && <Note kind="err">{a.error}</Note>}
      <button className="btn primary" disabled={a.busy || !acc}>Upgrade now</button>
      <small className="muted">Updates at once. Passing a phase or reaching Funded creates a certificate.</small>
    </form>
  );
}

function Withdraw({ accounts, wallets, done }: { accounts: Any[]; wallets: string[]; done: (msg: string) => void }) {
  const [f, setF] = useState({ login: accounts[0]?.login ?? '', amount: '', method: wallets[0] ?? 'USDT (TRC20)', destination: '' });
  const a = useAction();
  async function go(e: FormEvent) {
    e.preventDefault();
    const amount = Number(f.amount);
    if (await a.run(() => api.post('/v1/partner/marketing/withdrawals', { ...f, amount }))) { done(`Withdrawal of ${usd(amount)} approved and paid in a second. Your payout certificate is ready.`); setF({ ...f, amount: '' }); }
  }
  return (
    <form className="panel form" onSubmit={go}>
      <h3><Wallet />Withdraw</h3>
      <label>Account number<input className="mono" list="mk-logins" required value={f.login} onChange={(e) => setF({ ...f, login: e.target.value })} /></label>
      <div className="grid2">
        <label>Amount (USD)<input type="number" min={1} step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></label>
        <label>Method<select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{wallets.map((w) => <option key={w}>{w}</option>)}</select></label>
      </div>
      <label>{/USDT|USDC|BTC|ETH/.test(f.method) ? 'Wallet address' : 'Account or phone number'}<input className="mono" required value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} /></label>
      {a.error && <Note kind="err">{a.error}</Note>}
      <button className="btn primary" disabled={a.busy}>Withdraw now</button>
      <small className="muted">Marketing withdrawals are approved automatically within a second. No real money is sent.</small>
    </form>
  );
}

function Certificates({ list, name, tenant, accounts }: { list: Any[]; name: string; tenant: PublicTenant; accounts: Any[] }) {
  const data: RecordLite = {
    name, firm: tenant.branding.name, template: { heading: '', signatory: 'Collins Kessy', signatoryTitle: 'Founder & CEO', footer: '' },
    accounts: accounts.map((x) => ({ id: x.id, login: x.login, logins: [x.login], name: `${x.planName} ${x.size >= 1000 ? `$${x.size / 1000}K` : x.size}`, status: x.status, phase: x.phase })),
    events: list.map((c) => ({ type: c.kind, at: c.created_at, title: `${c.headline} certificate`, detail: c.label ?? '', certificate: true, ref: c.id, accountId: c.account_id ?? null, login: c.login ?? null, cert: { headline: c.headline, label: c.label ?? undefined, value: c.value ?? undefined } })),
  };
  return (
    <section className="panel"><h3><Award />Your certificates</h3>
      <CertificateBrowser data={data} logoUrl={tenant.branding.logoUrl} empty={<Empty title="No certificates yet.">Pass a phase, reach Funded or make a withdrawal and your certificate appears here, ready to post.</Empty>} />
    </section>
  );
}

/** Shown to a new IB until the affiliate team approves them. */
export function PendingApproval({ name, note, rejected, signOut }: { name: string; note: string | null; rejected: boolean; signOut: () => void }) {
  return (
    <div className="ib-pending">
      <div className="ib-pending-card">
        <span className="ib-pending-ic">{rejected ? '!' : <Clock />}</span>
        <h1>{rejected ? 'Your IB application was not approved' : `Welcome, ${name.split(' ')[0]}. You're almost in.`}</h1>
        <p>{rejected ? (note || 'Contact our support team if you think this is a mistake.') : 'Our affiliate team is reviewing your IB account. You get full access to your link, clients, commissions and marketing account as soon as you are approved, usually within one working day.'}</p>
        <ol className="ib-pending-steps"><li className="done"><i><CheckCircle2 /></i>Account created</li><li className={rejected ? '' : 'on'}><i>2</i>Review by our team</li><li><i>3</i>Full IB portal</li></ol>
        <button className="btn ghost" onClick={signOut}>Sign out</button>
      </div>
    </div>
  );
}
